<template>
  <div class="facilitated-session">
    <h1>{{ session.name }} <small class="text-muted">Facilitated Session</small></h1>
    <div class="row">
      <div class="col-md-5 col-lg-4 col-xl-3">
        <div class="card sticky-top" style="top:1rem;">
          <h5 class="card-header">Controls</h5>
          <div class="card-body">
            <p>Status: <strong>{{ paused ? 'Paused' : 'Running' }}</strong></p>
            <button v-if="!paused" class="btn btn-default btn-block" @click="togglePause(true)">Pause Session</button>
            <button v-if="paused" class="btn btn-default btn-block" @click="togglePause(false)">Resume Session</button>
            <hr>
            <h5>Gates</h5>
            <button class="btn btn-danger btn-block" @click="lockGates()">Lock All</button>
            <button class="btn btn-success btn-block" @click="unlockGates()">Unlock All</button>
            <table class="table table-bordered table-striped mt-3 mb-3">
              <thead>
              <tr>
                <th scope="col">Gate</th>
                <th scope="col">Title</th>
                <th scope="col">Delete</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(chapter, index) in chapters" :key="`gates-${chapter.id}`" class="mb-2" scope="row">
                <td class="text-center">
                  <div :class="!disabled_chapters.includes(chapter.id) ? '' : 'd-none'">
                    <input :id="`gates-${chapter.id}`" v-model="gates" :value="chapter.id" class="gate-checkbox"
                           type="checkbox" @change="toggleGate(chapter)">
                  </div>
                </td>
                <td>
                  <label :class="{ disabled : isDisabled(chapter.id) }" :for="`gates-${chapter.id}`"
                         class="chapter-label">
                    {{ chapter.title }}
                  </label>
                </td>
                <td class="text-center">
                  <div :class="!gates.includes(chapter.id) ? '' : 'd-none'">
                    <input v-if="index > 0" :id="`form-check-input-${ chapter.id }`" v-model="disabled_chapters"
                           :value="chapter.id" class="enable-disable-checkbox" type="checkbox"
                           @change="toggleChapter(chapter)">
                    <label v-if="index > 0" :for="`form-check-input-${ chapter.id }`" class="enable-disable-checker"><i
                        class="fa fa-times-circle"></i></label>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
            <button class="btn btn-warning btn-block" @click="resetClients()">Reset All Clients</button>
            <button class="btn btn-danger btn-block" @click="deleteRecords()">Delete All Records</button>
          </div>
        </div>
      </div>
      <div class="col-md-7 col-lg-8 col-xl-9">
        <ul class="nav nav-tabs">
          <li class="nav-item">
            <a id="progressAndScoringTab" class="nav-link active" data-toggle="tab" href="#progressAndScoringCard">Progress
              and Scoring</a>
          </li>
          <li class="nav-item">
            <a id="textEntriesTab" class="nav-link" data-toggle="tab" href="#textEntriesCard">Text Entries <span
                class="badge badge-pill badge-danger"> {{ countTotalQuestionEntries(sorted) }} </span></a>
          </li>
        </ul>
        <div class="tab-content">
          <div id="progressAndScoringCard" class="tab-pane card active">
            <vue-good-table :columns="columns"
                            :pagination-options="{ enabled: enablePagination, perPage: recordsPerPage, perPageDropdownEnabled: false }"
                            :row-style-class="rowStyleClassFn"
                            :rows="sorted"
                            :select-options="{ enabled: true, selectOnCheckboxOnly: true }"
                            styleClass="vgt-table table-striped"
                            @on-selected-rows-change="selectionChanged"
            >
              <template #table-row="props">
                <span v-if="props.column.field === 'team_name'">
                  <div>{{ props.row.team_name }}</div>
                </span>
                <span v-if="props.column.field === 'group'">
                  <div>{{ props.row.group ? props.row.group.name : 'Unknown' }}</div>
                </span>
                <span v-if="props.column.field === 'status'">
                  <div v-if="props.row.bookmark">
                    <div class="progress border border-primary" style="height:2rem;">
                      <div :style="`width: ${Math.round(((props.row.active_index + 1)/activePageCount) * 100)}%`"
                           aria-valuemax="100"
                           aria-valuemin="0"
                           aria-valuenow="0" class="progress-bar" role="progressbar">
                        <ProgressRatio :completed="props.row.completed" :locationIndex="props.row.active_index + 1"
                                       :pageCount="activePageCount" :pageType="props.row.bookmark.pageType"/>
                      </div>
                    </div>
                  </div>
                  <div v-else>Not Started</div>
                </span>
                <span v-if="props.column.field === 'score'">
                  <div>{{ props.row.composite_score || 0 }}</div>
                </span>
                <span v-if="props.column.field === 'controls'">
                  <button class="btn btn-primary reset-scenario-btn" data-backdrop="static"
                          data-keyboard="false" data-target="#modal-reset-scenario" data-toggle="modal"
                          @click="resetScenarioForTeam(props.row)"><i class="fas fa-lg fa-backward"></i></button>
                  <button class="btn btn-warning" @click="resetClients(props.row)">Reset</button>
                  <button class="btn btn-danger" @click="deleteRecords(props.row)">Delete</button>
                </span>
                <span v-if="props.column.field === 'meters'">
                  <div class="panel panel-default">
                    <button class="btn btn-primary metrics-popover-trigger mr-1" @click="toggleMeterPopover(props.row.id)">
                      <i class="fas fa-lg fa-tachometer-alt"></i>
                    </button>
                    <a class="btn btn-primary" :href="`/threads?record_id=${props.row.id}`" target="_blank">
                      <i class="fas fa-lg fa-comment"></i>
                    </a>
                    <div :id="`popover-content-${props.row.id}`" class="my-popover-content facilitator-meter-popover"
                         v-bind:class="[activeMeterPopover == props.row.id ? 'active' : '']">
                      <div class="arrow"></div>
                      <div v-if="metrics" class="meters-tooltip-body">
                        <div v-for="(metric, meterindex ) in metrics"
                             :key="metric.id"
                             class="meter-wrapper d-flex flex-row justify-content-around align-items-end flex-nowrap">
                          <div class="meter-minus"><a class="dec-btn"
                                                      href="#"
                                                      @click.prevent="decrementMeter(meterindex, props.row, metric)">(-)</a></div>
                          <div class="meter-min"> {{ metric.min }}</div>
                          <div class="meter-center">
                            <div class="meter-label">{{ metric.label }}</div>
                            <div class="progress">
                              <div class="meter-value" v-html="getMeterValue(props.row, metric)"></div>
                              <div :style="meterStyle(props.row, metric)" class="progress-bar" role="progressbar"></div>
                            </div>
                          </div>
                          <div class="meter-max">{{ metric.max }}</div>
                          <div class="meter-plus"><a class="inc-btn"
                                                     href="#"
                                                     @click.prevent="incrementMeter(meterindex, props.row, metric)">(+)</a></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </span>
              </template>
            </vue-good-table>
          </div>
          <div id="textEntriesCard" class="tab-pane card">
            <div class="card-header">
              <div aria-controls="collapseone" aria-expanded="true" class="row" data-target="#collapseone"
                   data-toggle="collapse">
                <div class="col-md-9">
                  <h5>Open Ended Questions</h5>
                </div>
                <div class="col-md-3">
                  <h5 class="d-inline">Status</h5>
                  <a :href="pdfReportUrl" class="btn btn-primary float-right" target="_blank"><i
                      class="fas fa-external-link-alt"></i></a>
                </div>
              </div>
            </div>
            <div v-for="(idealTextQuestion, index) in sessionQuestions" :key="`record-${index}`" class="card">
              <div :id="`accordion-${index}`" class="accordion-container">
                <div :id="`heading-${index}`" class="card-header">
                  <div class="row">
                    <div class="col-md-9">
                      <div class="question-text" v-html="idealTextQuestion.content.question.text">
                      </div>
                      <div>
                        <button class="btn btn-primary reset-scenario-btn" data-target="#modal-text-answer"
                                data-toggle="modal" v-on:click="buildAnswerList(idealTextQuestion.id)">View
                          Answers
                        </button>
                      </div>
                    </div>
                    <div class="col-md-2 status-col">
                      {{ completedQuestions(sorted, idealTextQuestion.id) }}/{{ sorted.length }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <div class="small">Status will update automatically, do not refresh this page.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- BEGIN TEXT ANSWER MODAL -->
    <div id="modal-text-answer" class="modal fade" role="dialog" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header" style="align-items: center;">
            <div v-if="session.groups && session.groups.length > 0" class="form-group mb-0 mr-4">
              <select id="group-filter-value" class="form-control" v-on:change="filterByGroup()">
                <option value="0">All Groups</option>
                <option v-for="group in session.groups" :value="group.id">{{ group.name }}</option>
              </select>
            </div>
            <h5 class="modal-title">Team Answers</h5>
            <button aria-label="Close" class="close" data-dismiss="modal" type="button">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-for="(answer, index) in modalListAnswers">
              <div class="card">
                <div class="card-header">
                  <strong>{{ answer.team_name }}</strong>
                </div>
                <div class="card-body">
                  <div :id="'response-' + index" v-html="renderResponse(answer.response, index)"></div>
                  <div v-if="answer.aiFeedback" class="mt-4">
                    <strong>AI Feedback</strong><br/>
                    <div v-html="answer.aiFeedback"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- END TEXT ANSWER MODAL -->

    <!-- BEGIN RESET SCENARIO MODAL -->
    <div id="modal-reset-scenario" class="modal fade" role="dialog" tabindex="-1">
      <div v-if="selectedTeamForScenarioReset" class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <p class="modal-title">Reset Scenario for <strong>{{ selectedTeamForScenarioReset.team_name }}</strong></p>
            <button aria-label="Close" class="close" data-dismiss="modal" type="button"
                    @click="dismissResetScenarioModal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="!this.interfaceLocked && !this.scenarioReset" class="alert alert-primary" role="alert">
              <p style="margin-bottom: 0;">Locking User Interface for {{ selectedTeamForScenarioReset.team_name }}
                <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="4" cy="12" r="3">
                    <animate id="a" attributeName="cy" begin="0;b.end+0.25s" calcMode="spline" dur="0.6s"
                             keySplines=".33,.66,.66,1;.33,0,.66,.33" values="12;6;12"/>
                  </circle>
                  <circle cx="12" cy="12" r="3">
                    <animate attributeName="cy" begin="a.begin+0.1s" calcMode="spline" dur="0.6s"
                             keySplines=".33,.66,.66,1;.33,0,.66,.33"
                             values="12;6;12"/>
                  </circle>
                  <circle cx="20" cy="12" r="3">
                    <animate id="b" attributeName="cy" begin="a.begin+0.2s" calcMode="spline" dur="0.6s"
                             keySplines=".33,.66,.66,1;.33,0,.66,.33" values="12;6;12"/>
                  </circle>
                </svg>
              </p>
            </div>
            <table v-if="this.interfaceLocked && !this.scenarioReset && !this.scenarioResetSubmitting"
                   class="table table-bordered table-striped mt-3 mb-3">
              <thead>
              <tr>
                <th scope="col">Scenario</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(chapter, index) in chapters" :key="`scenario-${chapter.id}`" :class="'scenario-' + index"
                  class="scenario"
                  scope="row">
                <td>
                  <button :disabled="index >= selectedRecordChapterIndex" class="btn btn-link"
                          @click="setScenarioId(chapter.id)">{{ chapter.title }}
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
            <div v-if="this.scenarioResetSubmitting" class="alert alert-primary" role="alert">
              <p style="margin-bottom: 0;">Resetting Scenario
                <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="4" cy="12" r="3">
                    <animate id="a" attributeName="cy" begin="0;b.end+0.25s" calcMode="spline" dur="0.6s"
                             keySplines=".33,.66,.66,1;.33,0,.66,.33" values="12;6;12"/>
                  </circle>
                  <circle cx="12" cy="12" r="3">
                    <animate attributeName="cy" begin="a.begin+0.1s" calcMode="spline" dur="0.6s"
                             keySplines=".33,.66,.66,1;.33,0,.66,.33"
                             values="12;6;12"/>
                  </circle>
                  <circle cx="20" cy="12" r="3">
                    <animate id="b" attributeName="cy" begin="a.begin+0.2s" calcMode="spline" dur="0.6s"
                             keySplines=".33,.66,.66,1;.33,0,.66,.33" values="12;6;12"/>
                  </circle>
                </svg>
              </p>
            </div>
            <div v-if="this.scenarioReset" class="alert alert-success" role="alert">Scenario Successfully Reset</div>
          </div>
          <div class="modal-footer">
            <button :disabled="this.scenarioIdToResetTo == null || this.scenarioResetSubmitting || this.scenarioReset"
                    class="btn btn-primary"
                    @click="submitScenarioReset">Submit
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- END RESET SCENARIO MODAL -->
  </div>
</template>

<script>
import _ from 'lodash'
import leaderboardMixin from '../mixins/leaderboard'
import ProgressRatio from './ProgressRatio.vue'
import {VueGoodTable} from 'vue-good-table-next'

export default {
  mixins: [leaderboardMixin],
  components: {ProgressRatio, VueGoodTable},
  data() {
    let metrics = _.get(this.session, 'publication.content.metrics', [])
    if (typeof metrics === 'string') {
      try {
        metrics = JSON.parse(metrics, (k, v) => {
          return (typeof v === 'object' || isNaN(v)) ? v : parseInt(v, 10)
        })
      } catch (err) {
        console.error(err)
      }
    }
    let chapters = _.cloneDeep(this.session.publication.content.chapters);
    let pages = _.flatMap(chapters, 'pages');
    let idealTextQuestionPages = _.filter(pages, (page) => {
      return page.layout === 'IdealTextQuestion' || page.layout === 'OpenResponseQuestion'
          || page.layout === 'OpenResponseQuestionWithFeedback' || page.layout === 'OpenResponseQuestionAI'
    })

    let questionEntries = _.reduce(this.session.records, (accum, current, index) => {
      console.log("QuestionEntries");
      if (current.bookmark && current.bookmark.textResponses) {
        const iterateeCount = _.keys(current.bookmark.textResponses).length;
        return accum + iterateeCount;
      }
      return accum;
    }, 0)
    const columns = [
      {
        label: 'Team',
        field: 'team_name',
        sortable: false,
        filterOptions: {
          enabled: true
        }
      },
      {
        label: 'Group',
        field: 'group',
        sortable: false,
        filterOptions: {
          enabled: true,
          filterFn: this.filterGroups
        }
      },
      {
        label: 'Status',
        field: 'status',
        sortable: false
      },
      {
        label: 'Score',
        field: 'score',
        sortable: false
      },
      {
        label: '',
        field: 'controls',
        sortable: false
      },
      {
        label: 'Meters/Threads',
        field: 'meters',
        sortable: false
      },
    ]
    return {
      paused: this.session.paused,
      gates: this.session.gates || [],
      metrics: metrics,
      meters: {},
      sessionQuestions: idealTextQuestionPages,
      disabled_chapters: this.session.disabled_chapters || [],
      questionEntries,
      toggleMeterToolTip: false,
      activeMeterPopover: null,
      selectedTeamForScenarioReset: null,
      scenarioIdToResetTo: null,
      interfaceLocked: false,
      scenarioReset: false,
      scenarioResetSubmitting: false,
      selectedRecordChapterIndex: 0,
      columns: columns,
      recordsPerPage: 25,
      modalListAnswers: null,
      groupFilter: null,
      currentTextAnswerPageId: null,
      selectedTeamIds: []
    }
  },
  methods: {
    lockGates() {
      console.info('Locking all gates.')
      this.gates = _.map(this.chapters, 'id')
      this.updateSession()
    },
    toggleMeterPopover(index) {
      if (this.activeMeterPopover === index) {
        this.activeMeterPopover = null
      } else {
        this.activeMeterPopover = index
      }
    },
    incrementMeter(meterindex, record, metric) {

      let currentValue = this.getMeterValue(record, metric)
      // don't exceed max meter val
      if (currentValue === metric.max) {
        return false;
      }

      record.bookmark.metrics[meterindex].value = currentValue + 1

      // update meter on the team record
      _.debounce(() => {
        axios.patch(`/api/record/${record.id}/metrics`, {metrics: record.bookmark.metrics})
      }, 200)()

    },
    decrementMeter(meterindex, record, metric) {

      let currentValue = this.getMeterValue(record, metric)
      // don't go below min meter value
      if (currentValue === metric.min) {
        return false;
      }

      record.bookmark.metrics[meterindex].value = currentValue - 1

      // update meter the team record
      _.debounce(() => {
        axios.patch(`/api/record/${record.id}/metrics`, {metrics: record.bookmark.metrics})
      }, 200)()

    },
    completedQuestions(records, pageID) {
      return _.reduce(records, (a, v, i) => {
        //if we have an answer then increment the accumulator
        //otherwise return the accumulator
        if (v.bookmark && v.bookmark.textResponses) {
          (!_.isUndefined(v.bookmark.textResponses[pageID])) ? a++ : a;
        }
        return a;
      }, 0)
    },
    getTeamTextResponse(record, pageID) {
      if (record.bookmark && record.bookmark.textResponses) {
        console.log("TEAM RESPONSE");
        return record.bookmark.textResponses[pageID];
      }
    },
    countTotalQuestionEntries(records) {
      let totalEntries = 0;
      _.each(records, function (record) {
        if (record.bookmark !== null) {
          if (typeof record.bookmark.textResponses != 'undefined') {
            totalEntries = totalEntries + Object.keys(record.bookmark.textResponses).length
          }
        }
      })
      return totalEntries
    },
    getTextResponseFromRecordByPageID(record, pageID) {
      return record?.bookmark?.textResponses?.[pageID]?.textResponse || null
    },
    getAIFeedbackFromRecordByPageID(record, pageID) {
      return record?.bookmark?.textResponses?.[pageID]?.aiFeedback || null
    },
    getRecordMetricScoresByPageID(record, pageID) {
      try {
        let scores = {}
        _.each(this.metrics, (meter) => {
          scores[meter.id] = {
            label: meter.label,
            score: record.bookmark.textResponses[pageID]['meterScores'][meter.id]['score'],
            color: this.getMeterColor(meter.id)
          }
        })
        return scores
      } catch (e) {
        return null
      }
    },
    unlockGates() {
      console.info('Unlocking all gates.')
      this.gates = []
      this.updateSession()
    },
    isDisabled(id) {
      return _.includes(this.disabled_chapters, id)
    },
    toggleGate(chapter) {
      const index = this.gates.indexOf(chapter.id)

      if (index >= 0) {
        console.info('Gate locked:', chapter)
      } else {
        console.info('Gate unlocked:', chapter)
      }
      this.updateSession()
    },
    togglePause(value) {
      if (value) {
        console.info('Pausing session!')
      } else {
        console.info('Resuming session!')
      }
      this.paused = value
      this.updateSession()
    },
    toggleChapter(chapter) {
      this.updateSession()
    },
    resetClients(record) {
      if (!confirm('Are you sure you want to RESET? This action is not reversible.')) {
        return
      }

      const data = {
        session_id: this.session.id
      }

      if (record) {
        console.info('Resetting clients:', record)
        data.record_id = record.id
      } else {
        console.info('Resetting all clients!')
      }

      axios.post(`/api/reset`, data)
          .then((response) => {
            this.records = response.data.session.records
            this.enableChapterCheckboxes()
            this.disableChapterCheckbox(response.data.session.furthest_chapter_reached)
          })
          .catch((err) => {
            console.error(err)
          })
    },
    deleteRecords(record) {
      if (!confirm('Are you sure you want to DELETE? This action is not reversible.')) {
        return
      }

      const data = {
        session_id: this.session.id
      }

      if (record) {
        console.info('Deleting records:', record)
        data.record_id = record.id
      } else {
        console.info('Deleting all records!')
      }

      axios.post(`/api/delete`, data)
          .then(({data}) => {
            if (data.success) {
              console.log('records deleted')
              this.records = data.session.records
              this.enableChapterCheckboxes()
              this.disableChapterCheckbox(data.session.furthest_chapter_reached)
            }
          })
          .catch((err) => {
            console.error(err)
          })
    },
    updateSession() {
      axios.patch(`/sessions/${this.session.id}`, {
        gates: this.gates,
        paused: this.paused,
        disabled_chapters: this.disabled_chapters
      })
          .then((response) => {
            this.calcActivePageCount(this.disabled_chapters)
          })
          .catch((err) => {
            console.error(err)
          })
    },
    getMeter(record, id) {
      const meters = _.get(record, 'bookmark.metrics', []);
      return _.find(meters, {id: id})
    },
    getMeterValue(record, metric) {
      const meter = this.getMeter(record, metric.id);
      return meter ? meter.value : false;
    },
    getMeterColor(meterID) {
      // get brand color
      let color = '#999'
      if (this.brandColors) {
        if (meterID === 'meter-1') {
          color = this.brandColors.primary
        } else if (meterID === 'meter-2') {
          color = this.brandColors.secondary
        } else if (meterID === 'meter-3') {
          color = this.brandColors.tertiary
        } else if (meterID === 'meter-4') {
          color = this.brandColors.meter4
        } else if (meterID === 'meter-5') {
          color = this.brandColors.meter5
        }
      }
      return color
    },
    meterStyle(record, metric) {
      const meter = this.getMeter(record, metric.id)
      if (meter) {
        // get meter width
        const minMod = (0 - meter.min)
        const percent = (meter.value + minMod) / (meter.max + minMod)
        let color = this.getMeterColor(metric.id)
        return `width: ${percent * 100}%; background: ${color};`
      }
      return false;
    },
    resetScenarioForTeam(record) {
      this.scenarioIdToResetTo = null
      this.selectedTeamForScenarioReset = record
      this.selectedRecordChapterIndex = this.chapters.findIndex((item) => {
        return item.id === record.chapter_id
      }) + 1
      let data = {}
      axios.post(`/api/record/${this.selectedTeamForScenarioReset.id}/lock`, data)
          .then(({data}) => {
            if (data.success) {
              console.log(data)
              this.interfaceLocked = true
            }
          })
          .catch((err) => {
            console.error(err)
          })

    },
    setScenarioId(id) {
      this.scenarioIdToResetTo = id
    },
    submitScenarioReset() {
      if (!this.interfaceLocked) {
        alert("team's interface failed to lock")
      }
      console.log('resetting scenario for team ', this.selectedTeamForScenarioReset.team_name)
      console.log('resetting to scenario ', this.scenarioIdToResetTo)
      this.scenarioResetSubmitting = true

      let data = {chapterId: this.scenarioIdToResetTo}
      axios.post(`/api/record/${this.selectedTeamForScenarioReset.id}/reset-scenario`, data)
          .then(({data}) => {
            if (data.success) {
              this.scenarioResetSubmitting = false
              this.scenarioReset = true
              setTimeout(() => {
                $('#modal-reset-scenario').modal('hide')
                this.scenarioReset = false
                this.unlockInterface()
                this.refreshRecordById(this.selectedTeamForScenarioReset.id)
              }, 1000)
            }
          })
          .catch((err) => {
            console.error(err)
          })

    },
    unlockInterface() {
      axios.post(`/api/record/${this.selectedTeamForScenarioReset.id}/unlock`)
          .then(() => {

          })
          .catch((err) => {
            console.error(err)
          })
    },
    dismissResetScenarioModal() {
      this.unlockInterface()
      this.interfaceLocked = false
      this.scenarioReset = false
    },
    filterGroups(row, filterString) {
      if (row && row.name) {
        return row.name.toLowerCase() === filterString.toLowerCase()
      } else {
        return true
      }
    },
    rowStyleClassFn(row) {
      return row.completed ? 'table-secondary' : ''
    },
    filterByGroup() {
      let value = document.getElementById('group-filter-value')
      this.buildAnswerList(this.currentTextAnswerPageId, parseInt(value.value))
      console.log('filter by group - ' + value.value)
    },
    buildAnswerList(pageId, filter = 0) {
      this.currentTextAnswerPageId = pageId
      this.modalListAnswers = []
      this.sorted.forEach((record) => {
        let teamResponse = this.getTextResponseFromRecordByPageID(record, pageId)
        let aiFeedback = this.getAIFeedbackFromRecordByPageID(record, pageId)

        if (teamResponse !== null) {
          if (filter !== 0) {
            if (record.group_id == filter) {
              this.modalListAnswers.push({
                team_name: record.team_name,
                response: teamResponse,
                aiFeedback: aiFeedback
              })
            }
          } else {
            this.modalListAnswers.push({
              team_name: record.team_name,
              response: teamResponse,
              aiFeedback: aiFeedback
            })
          }
        }
      })
    },
    selectionChanged({selectedRows}) {
      this.selectedTeamIds = selectedRows.map((row) => {
        return row.id
      })
    },
    renderResponse: async function (response, index) {
      if (response.indexOf('thread_') !== 0) {
        setTimeout(() => {
          let el = document.getElementById('response-' + index)
          if (el) {
            el.innerHTML = response
          }
        }, 50)
        return response
      }
      let resp = await axios.get(`//${window.location.hostname}/api/openai/thread/${response}/messages`)
      resp.data.data.reverse()
      let output = ''
      if (resp.data.data.length > 0) {
        resp.data.data.forEach((message, index) => {
          let role = (index === 0) ? 'assistant' : message.role
          output += `<div class="mb-4"><strong>${role}</strong>: ${message.content[0].text.value}</div>`
        })
      } else {
        output = 'No response'
      }
      let el = document.getElementById('response-' + index)
      if (el) {
        el.innerHTML = output
      }
      return output
    }
  },
  computed: {
    enablePagination: function () {
      return this.sorted.length > this.recordsPerPage
    },
    pdfReportUrl: function () {
      let query = ''
      if (this.selectedTeamIds.length > 0) {
        query = '?teamIds=' + this.selectedTeamIds.join(',')
      }
      return `/sessions/${this.session.id}/facilitate/pdf${query}`
    }
  },
  mounted() {
    this.disableChapterCheckbox(this.session.furthest_chapter_reached)
  }

}
</script>

<style lang="scss">
#progressAndScoringCard {
  .vgt-table {
    border: none !important;
  }

  .vgt-responsive {
    overflow-x: visible;
  }
}

#textEntriesCard {
  > .card {
    border-left: none;
    border-right: none;
  }

  .card {
    border-left: none;
    border-right: none;

    .card:last-of-type {
      border-bottom: none;
      margin-bottom: 0;
    }
  }

  .card-header {
    cursor: pointer;
  }

  .chevron {
    border: none;
    cursor: pointer;

    i.fas:before {
      content: "\F13A"
    }

    &.collapsed {
      i.fas:before {
        content: "\F139"
      }
    }

    &:focus {
      border: none;
      outline: none;
    }
  }

  .ideal-answer {
    font-style: italic;
    margin-bottom: 0;
  }

  .ideal-answer, .question-text {
    font-size: 1.1rem;
  }

  .ideal-answer-label {
    border-bottom: 1px solid rgba(0, 0, 0, 0.25);
    display: block;
    margin: 20px 0;
  }
}

.facilitator-meter-popover {
  .progress {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.chapter-label {
  margin-bottom: 0;
}

.gate-checkbox {
  height: 15px;
  width: 15px;
}

.enable-disable-checkbox {
  display: none;
  height: 25px;
  width: 25px;
}

.enable-disable-checker {
  font-size: 25px;
  margin: 0;

  &:hover {
    cursor: pointer;
  }
}

.table-striped td {
  height: 58px;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.1);
}

.table-striped tbody tr:nth-of-type(even) .enable-disable-checker {
  color: rgba(0, 0, 0, 0.4);
}

.table-striped tbody tr:nth-of-type(odd) .enable-disable-checker {
  color: #FFF;
}

.enable-disable-checkbox:checked + .enable-disable-checker i {
  color: red;
}

.enable-disable-checkbox:disabled + .enable-disable-checker i {
  opacity: 0.4;

  &:hover {
    cursor: none;
  }
}

#metrics-cell {
  position: relative;
}

.my-popover-content {
  background: #FFF;
  border: 1px solid rgba(0, 0, 0, 0.2);
  display: none;
  margin-top: 14px;
  padding: 10px;
  position: absolute;
  width: 200px;

  &.active {
    display: block;
    margin-left: -76px;
  }
}

.my-popover-content > .arrow {
  background: #FFF;
  border-bottom-color: rgba(0, 0, 0, 0.25);
  border-color: transparent;
  border-left: 1px solid rgba(0, 0, 0, 0.25);
  border-style: solid;
  border-top: 1px solid rgba(0, 0, 0, 0.25);
  border-top-width: 0;
  border-width: 11px;
  display: block;
  height: 20px;
  left: 98px;
  margin-left: -11px;
  position: absolute;
  top: -10px;
  transform: rotate(45deg);
  width: 20px;
  z-index: 1000;
}

.my-popover-content > .arrow:after {
  border-bottom-color: #fff;
  border-color: transparent;
  border-style: solid;
  border-top-width: 0;
  border-width: 10px;
  content: " ";
  display: block;
  height: 0;
  margin-left: -10px;
  position: absolute;
  top: 1px;
  width: 0;
}

.inc-btn, .dec-btn {
  position: relative;
  z-index: 1000;
}

.meters-tooltip-body {
  min-width: 150px;
}

.meter-wrapper {
  margin: 5px 0;

  .meter-label {
    font-size: 0.875rem;
    line-height: 1.25rem;
    text-align: center;
  }

  .meter-minus,
  .meter-plus,
  .meter-min,
  .meter-max {
    font-size: 0.75rem;
    margin: 0 2px;
  }

  .meter-value {
    color: #fff;
    font-size: 0.75rem;
    left: 0;
    margin-top: 7px;
    position: absolute;
    text-align: center;
    width: 100%
  }

  .meter-center {
    width: 60%
  }
}

#modal-reset-scenario {
  .modal-body {
    height: 400px;
    overflow: auto;
  }

  .scenario {
    &.scenario-0 {
      display: none;
    }

    &:hover {
      cursor: pointer;
    }
  }
}

.status-col {
  font-size: 1.25rem;
}
</style>
